import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      // Merge any anonymous hearts from this browser into the new
      // account so nothing gets lost on sign-up.
      try {
        const { syncWishlistLocalToServer } = await import('../components/WishlistHeart');
        syncWishlistLocalToServer();
      } catch { /* silent */ }
    }
    return response.data;
  },

  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      try {
        const { syncWishlistLocalToServer } = await import('../components/WishlistHeart');
        syncWishlistLocalToServer();
      } catch { /* silent */ }
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Properties API
export const propertiesAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    const response = await api.get(`/properties?${params.toString()}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/properties/${id}`);
    return response.data;
  },

  create: async (propertyData) => {
    const response = await api.post('/properties', propertyData);
    return response.data;
  },

  update: async (id, propertyData) => {
    const response = await api.put(`/properties/${id}`, propertyData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/properties/${id}`);
    return response.data;
  },

  incrementView: async (id) => {
    const response = await api.post(`/properties/${id}/view`);
    return response.data;
  },
};

// Saved Properties API
export const savedAPI = {
  getAll: async () => {
    const response = await api.get('/saved');
    return response.data;
  },

  save: async (propertyId) => {
    const response = await api.post(`/saved/${propertyId}`);
    return response.data;
  },

  unsave: async (propertyId) => {
    const response = await api.delete(`/saved/${propertyId}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getMyListings: async () => {
    const response = await api.get('/dashboard/my-listings');
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
};

// Subscriptions API
export const subscriptionsAPI = {
  getPlans: async () => {
    const response = await api.get('/subscriptions/plans');
    return response.data;
  },

  getCurrent: async () => {
    const response = await api.get('/subscriptions/current');
    return response.data;
  },

  create: async (planId) => {
    const response = await api.post('/subscriptions/create', null, {
      params: { plan_id: planId }
    });
    return response.data;
  },

  upgrade: async (newPlanId) => {
    const response = await api.post('/subscriptions/upgrade', null, {
      params: { new_plan_id: newPlanId }
    });
    return response.data;
  },

  downgrade: async (newPlanId) => {
    const response = await api.post('/subscriptions/downgrade', null, {
      params: { new_plan_id: newPlanId }
    });
    return response.data;
  },

  cancel: async (immediate = false) => {
    const response = await api.post('/subscriptions/cancel', null, {
      params: { immediate }
    });
    return response.data;
  },

  reactivate: async () => {
    const response = await api.post('/subscriptions/reactivate');
    return response.data;
  },
};

export default api;
